import { Backdrop, CircularProgress } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  User,
  X,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../../API/newIndex";
import generatePin from "../../../controllers/generatePin";
import useUnsavedChanges from "../../../controllers/useUnsavedChanges";
import UnsavedChangesModal from "../../../controllers/UnsavedChangesModal";
import { ButtonComponent, InputComponent, SelectComponent } from "@shopeasy-dashboard-ui/ui-library";
import Notification from "../../Notification";

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (JSON.stringify(cur) !== JSON.stringify(orig)) {
      changed[key] = cur;
    }
  });
  return changed;
};

const BulkEditPosStaff = ({
  bulkStaffIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkStaffsList, setBulkStaffsList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [staffSearchTerm, setStaffSearchTerm] = useState("");
  const bulkStaffsDataRef = useRef({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formSpinner, setFormSpinner] = useState(false);

  // Exact Form State matching UpdatePosStaff.js
  const [staffData, setStaffData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    pin: "",
    pos_location: [],
  });
  const [validEmailErr, setValidEmailErr] = useState(false);
  const [locationData, setLocationData] = useState([]);
  const [warn, setWarn] = useState(false);
  const [err, setErr] = useState(false);

  const originalDataRef = useRef(null);
  const originalPayloadRef = useRef(null);

  const buildPayload = (formData = {}) => {
    return {
      first_name: formData.first_name || "",
      last_name: formData.last_name || "",
      email: formData.email || "",
      phone: formData.phone || "",
      pin: formData.pin ? String(formData.pin) : "",
      pos_location: Array.isArray(formData.pos_location)
        ? formData.pos_location.map((item) => (typeof item === "object" ? item?.value || item?._id : item))
        : [],
    };
  };

  const currentFormValues = useMemo(() => {
    return { ...staffData };
  }, [staffData]);

  const getLocation = async () => {
    try {
      const res = await API({ url: "/admin/pos/getPOSLocations", method: "get" });
      if (res?.data) {
        const list = res.data?.data || res.data || [];
        setLocationData(list);
      }
    } catch (error) {
      console.error("Error fetching locations for staff dropdown:", error);
    }
  };

  const applyStaffToForm = (rawStaff) => {
    if (!rawStaff) return;

    let assignedLocations = [];
    if (Array.isArray(rawStaff?.pos_location)) {
      assignedLocations = rawStaff.pos_location.map((l) => (typeof l === "object" ? l._id || l.value : l));
    } else if (Array.isArray(rawStaff?.pos_location_info)) {
      assignedLocations = rawStaff.pos_location_info.map((l) => l._id || l);
    }

    const initialFormValues = {
      first_name: rawStaff?.first_name || "",
      last_name: rawStaff?.last_name || "",
      email: rawStaff?.email || "",
      phone: rawStaff?.phone || "",
      pin: rawStaff?.pin || "",
      pos_location: assignedLocations,
    };

    originalDataRef.current = initialFormValues;
    originalPayloadRef.current = buildPayload(initialFormValues);

    setStaffData(initialFormValues);
    setValidEmailErr(false);
    setWarn(false);
    setErr(false);
  };

  const fetchStaffsData = async () => {
    if (!bulkStaffIds || bulkStaffIds.length === 0) return;
    setFormSpinner(true);
    try {
      let res;
      try {
        res = await API({
          url: "/admin/pos/GetPOSStaffDataByIds",
          method: "post",
          data: { ids: bulkStaffIds, id: bulkStaffIds, staffs: bulkStaffIds, staff: bulkStaffIds },
        });
      } catch (postErr) {
        res = await API({
          url: "/admin/pos/GetPOSStaffDataByIds",
          method: "post",
          data: bulkStaffIds,
        });
      }

      let loadedRecords = [];
      if (res?.status === 200 || res?.status === 304) {
        const resultData = res?.data;
        loadedRecords =
          resultData?.data?.records ||
          resultData?.records ||
          (Array.isArray(resultData?.data) ? resultData.data : []) ||
          (Array.isArray(resultData) ? resultData : []);
      }

      if (loadedRecords.length > 0) {
        setBulkStaffsList(loadedRecords);
        applyStaffToForm(loadedRecords[0]);
      } else {
        setIsNotification(true);
        setNotificationMsg("No Staff records found for bulk edit");
        setSeverity("error");
      }
    } catch (error) {
      console.error("Error fetching bulk Staff data:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Failed to fetch Staff details");
      setSeverity("error");
    } finally {
      setFormSpinner(false);
    }
  };

  useEffect(() => {
    getLocation();
    fetchStaffsData();
  }, []);

  const emailVerify = async () => {
    if (staffData?.email?.length >= 3) {
      try {
        const storeApiData = await API({
          url: `/super/admin/tenant/validemail/${staffData?.email}`,
          method: "get",
        });
        if (storeApiData.status === 200 || storeApiData.status === 304) {
          setValidEmailErr(false);
        }
      } catch (error) {
        setValidEmailErr(true);
      }
    }
  };

  const saveCurrentStaffChangesToMemory = useCallback(() => {
    const activeItem = bulkStaffsList[activeBulkIndex];
    if (!activeItem?._id) return;

    const currentPayload = buildPayload(currentFormValues);
    const origPayload = originalPayloadRef.current || {};
    const changedFields = getChangedFields(currentPayload, origPayload);

    if (Object.keys(changedFields).length > 0) {
      bulkStaffsDataRef.current[activeItem._id] = {
        _id: activeItem._id,
        id: activeItem._id,
        values: { ...currentFormValues },
        originalPayload: origPayload,
        _hasChanged: true,
      };
    } else {
      delete bulkStaffsDataRef.current[activeItem._id];
    }
  }, [activeBulkIndex, bulkStaffsList, currentFormValues]);

  const switchActiveStaff = (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= bulkStaffsList.length) return;
    if (targetIndex === activeBulkIndex) return;

    saveCurrentStaffChangesToMemory();

    const targetItem = bulkStaffsList[targetIndex];
    const cachedChanges = bulkStaffsDataRef.current[targetItem?._id];

    if (cachedChanges?.values) {
      let assignedLocations = [];
      if (Array.isArray(targetItem?.pos_location)) {
        assignedLocations = targetItem.pos_location.map((l) => (typeof l === "object" ? l._id || l.value : l));
      } else if (Array.isArray(targetItem?.pos_location_info)) {
        assignedLocations = targetItem.pos_location_info.map((l) => l._id || l);
      }

      const orig = {
        first_name: targetItem?.first_name || "",
        last_name: targetItem?.last_name || "",
        email: targetItem?.email || "",
        phone: targetItem?.phone || "",
        pin: targetItem?.pin || "",
        pos_location: assignedLocations,
      };
      originalDataRef.current = orig;
      originalPayloadRef.current = buildPayload(orig);

      setStaffData(cachedChanges.values);
      setValidEmailErr(false);
      setWarn(false);
      setErr(false);
    } else {
      applyStaffToForm(targetItem);
    }

    setActiveBulkIndex(targetIndex);
  };

  const hasFormLevelChanges = useMemo(() => {
    if (!originalPayloadRef.current) return false;
    const currentPayload = buildPayload(currentFormValues);
    const changed = getChangedFields(currentPayload, originalPayloadRef.current);
    return Object.keys(changed).length > 0;
  }, [currentFormValues]);

  const isStaffModified = (staff, index) => {
    if (!staff) return false;
    if (bulkStaffsDataRef.current[staff._id]?._hasChanged) return true;
    if (index === activeBulkIndex && hasFormLevelChanges) return true;
    return false;
  };

  const modifiedCount = useMemo(() => {
    const memoryKeys = new Set(Object.keys(bulkStaffsDataRef.current));
    const activeStaff = bulkStaffsList[activeBulkIndex];
    if (activeStaff?._id && hasFormLevelChanges) {
      memoryKeys.add(activeStaff._id);
    } else if (activeStaff?._id && !hasFormLevelChanges && memoryKeys.has(activeStaff._id)) {
      memoryKeys.delete(activeStaff._id);
    }
    return memoryKeys.size;
  }, [bulkStaffsList, activeBulkIndex, hasFormLevelChanges]);

  const hasAnyBulkChanges = modifiedCount > 0;

  const {
    showUnsavedModal,
    handleRequestClose,
    handleConfirmLeave,
    handleStay,
  } = useUnsavedChanges(hasAnyBulkChanges, onClose);

  const handleSubmitBulkStaffs = async (e) => {
    if (e) e.preventDefault();

    saveCurrentStaffChangesToMemory();

    const activeItem = bulkStaffsList[activeBulkIndex];
    if (activeItem?._id && hasFormLevelChanges) {
      const currentPayload = buildPayload(currentFormValues);
      bulkStaffsDataRef.current[activeItem._id] = {
        _id: activeItem._id,
        id: activeItem._id,
        values: { ...currentFormValues },
        originalPayload: originalPayloadRef.current,
        _hasChanged: true,
      };
    }

    const modifiedEntries = Object.entries(bulkStaffsDataRef.current).filter(
      ([, data]) => data && data._hasChanged
    );

    if (modifiedEntries.length === 0) {
      setIsNotification(true);
      setNotificationMsg("No changes detected to update");
      setSeverity("info");
      return;
    }

    // Validation
    for (const [, data] of modifiedEntries) {
      if (!data.values.email || !data.values.email.trim() || !data.values.pin || !data.values.pin.toString().trim()) {
        setIsNotification(true);
        setNotificationMsg("All modified staff members must have Email and PIN");
        setSeverity("error");
        setWarn(true);
        setErr(true);
        return;
      }
    }

    setBtnLoading(true);
    setFormSpinner(true);
    try {
      const bulkPayload = modifiedEntries.map(([staffId, data]) => {
        const payload = buildPayload(data.values);
        const baseline = data.originalPayload;
        const changed = getChangedFields(payload, baseline);
        const fieldsToUpdate = changed && Object.keys(changed).length > 0 ? changed : payload;

        return {
          _id: staffId,
          id: staffId,
          ...fieldsToUpdate,
        };
      });

      let res;
      try {
        res = await API({
          url: "/admin/pos/BulkUpdatePOSStaff",
          method: "post",
          data: {
            staffs: bulkPayload,
            data: bulkPayload,
            staff: bulkPayload,
          },
        });
      } catch (err) {
        res = await API({
          url: "/admin/pos/BulkUpdatePOSStaff",
          method: "post",
          data: bulkPayload,
        });
      }

      if (res?.status === 200 || res?.status === 304 || res?.data?.success) {
        setIsNotification(true);
        setNotificationMsg(
          `${bulkPayload.length} Staff member${bulkPayload.length > 1 ? "s" : ""} updated successfully!`
        );
        setSeverity("success");
        bulkStaffsDataRef.current = {};
        if (onSuccess) {
          onSuccess(bulkPayload.length);
        } else if (onClose) {
          onClose();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to bulk update staff");
      }
    } catch (error) {
      console.error("Bulk update error:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Error updating staff");
      setSeverity("error");
    } finally {
      setBtnLoading(false);
      setFormSpinner(false);
    }
  };

  const filteredStaffsList = useMemo(() => {
    if (!staffSearchTerm.trim()) return bulkStaffsList;
    const term = staffSearchTerm.toLowerCase();
    return bulkStaffsList.filter((st) => {
      const name = `${st?.first_name || ""} ${st?.last_name || ""} ${st?.name || ""}`.toLowerCase();
      const stEmail = (st?.email || "").toLowerCase();
      return name.includes(term) || stEmail.includes(term);
    });
  }, [bulkStaffsList, staffSearchTerm]);

  const locationOptions = useMemo(() => {
    return (locationData || []).map((item) => ({
      value: item?._id,
      label: item?.name,
    }));
  }, [locationData]);

  const activeStaff = bulkStaffsList[activeBulkIndex];
  const activeStaffName = `${staffData.first_name || ""} ${staffData.last_name || ""}`.trim() || activeStaff?.name || "Staff Member";

  const handleCloseNotification = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] select-none">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleCloseNotification} severity={severity} />
      )}

      {formSpinner && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={formSpinner}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm">{t("lang_loading") || "Processing..."}</p>
        </Backdrop>
      )}

      {/* Top Header Bar matching FAQ */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--border-color)] bg-[var(--secondary-bg-color)] shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => handleRequestClose()}
            className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--table-text-color)] hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)] transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h5
                style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 700 }}
                className="!text-sm !font-bold text-[var(--primary-text-color)] m-0 !leading-5"
              >
                {t("lang_bulk_edit_staff", "Bulk Edit Staff")}
              </h5>
              <span className="bg-[var(--focus-tab-color)] text-[var(--primary-text-color)] text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--border-color)]">
                {bulkStaffsList.length} {t("lang_selected", "Selected")}
              </span>
            </div>
            <p className="text-[11px] text-[var(--table-text-color)] m-0 mt-0.5">
              Select any staff member from the list to view and edit its details.
            </p>
          </div>
        </div>

      </div>

      {/* Main Body (Split Layout) */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-[var(--border-color)] bg-[var(--primary-bg-color)] p-4 flex flex-col shrink-0">
          {/* Outer Rounded Container */}
          <div className="flex-1 flex flex-col border border-[var(--border-color)] rounded-2xl bg-[var(--secondary-bg-color)] overflow-hidden shadow-[var(--shadow-card)]">
            {/* Header section */}
            <div className="p-3.5 border-b border-[var(--border-color)] flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--primary-text-color)]">
                Selected Staff
              </span>
              <div className="flex items-center space-x-1 text-xs text-[var(--table-text-color)]">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActiveStaff(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-[var(--table-text-color)]">
                  {activeBulkIndex + 1}/{bulkStaffsList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkStaffsList.length - 1}
                  onClick={() => switchActiveStaff(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-[var(--border-color)] bg-transparent">
              <input
                type="text"
                placeholder="Filter selected staff..."
                value={staffSearchTerm}
                onChange={(e) => setStaffSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-[var(--border-color)] rounded-lg bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] placeholder-[var(--table-text-color)] focus:outline-none focus:border-[var(--focus-border-color)] shadow-xs"
              />
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredStaffsList.map((st) => {
                const actualIndex = bulkStaffsList.findIndex((item) => item._id === st._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isStaffModified(st, actualIndex);
                const displayName = isActive
                  ? `${staffData.first_name || ""} ${staffData.last_name || ""}`.trim() || st.name || "Untitled Staff"
                  : `${bulkStaffsDataRef.current[st._id]?.values?.first_name || st.first_name || ""} ${bulkStaffsDataRef.current[st._id]?.values?.last_name || st.last_name || ""}`.trim() || st.name || "Untitled Staff";
                const displayEmail = isActive ? staffData.email || st.email : bulkStaffsDataRef.current[st._id]?.values?.email || st.email || "No Email";

                return (
                  <div
                    key={st._id}
                    onClick={() => switchActiveStaff(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-[var(--border-color)] last:border-b-0 ${isActive
                        ? "bg-[var(--focus-tab-color)]"
                        : "bg-[var(--secondary-bg-color)] hover:bg-[var(--focus-tab-color)]"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-md bg-[var(--primary-bg-color)] border border-[var(--border-color)] flex items-center justify-center shrink-0 overflow-hidden p-1 text-[var(--table-text-color)]">
                        <User size={18} />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={`text-xs truncate ${isActive
                              ? "font-bold text-[var(--primary-text-color)]"
                              : "font-medium text-[var(--primary-text-color)]"
                            }`}
                        >
                          {displayName}
                        </span>
                        <span className="text-[11px] text-[var(--table-text-color)] truncate mt-0.5">
                          {displayEmail}
                        </span>
                      </div>
                    </div>

                    {isModified && (
                      <span
                        title='Modified'
                        className={`w-2 h-2 rounded-full bg-amber-500 shrink-0 select-none inline-block`}
                      />
                    )}
                  </div>
                );
              })}

              {filteredStaffsList.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--table-text-color)]">
                  No staff matching &quot;{staffSearchTerm}&quot;
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Main Form Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[var(--primary-bg-color)]">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-[var(--secondary-bg-color)] border-b border-[var(--border-color)] text-xs shrink-0">
            <div>
              <span className="font-bold text-[var(--primary-text-color)]">
                Currently Editing: {activeStaffName}
              </span>{" "}
              <span className="text-[var(--table-text-color)] font-mono text-[11px]">
                ({activeBulkIndex + 1} of {bulkStaffsList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-500 font-medium text-xs">
                  {modifiedCount} staff modified
                </span>
              ) : (
                <span className="text-[var(--table-text-color)] text-xs font-normal">
                  No unsaved changes
                </span>
              )}
            </div>
          </div>

          {/* Form Area matching UpdatePosStaff */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.preventDefault()}
              className="max-w-4xl mx-auto space-y-5"
            >
              <div className="bg-[var(--secondary-bg-color)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-card)] space-y-4">
                <InputComponent
                  id="first_name"
                  name="first_name"
                  label={t("lang_firstname")}
                  value={staffData?.first_name}
                  onChange={(e) => {
                    setStaffData({ ...staffData, first_name: e.target.value });
                    setWarn(false);
                    setErr(false);
                  }}
                  placeholder={t("lang_Write_Your_First_Name")}
                />

                <InputComponent
                  id="last_name"
                  name="last_name"
                  label={t("lang_lastname")}
                  value={staffData?.last_name}
                  onChange={(e) => {
                    setStaffData({ ...staffData, last_name: e.target.value });
                    setWarn(false);
                    setErr(false);
                  }}
                  placeholder={t("lang_Write_Your_Last_Name")}
                />

                <InputComponent
                  id="phone"
                  name="phone"
                  label={t("lang_phone")}
                  value={staffData?.phone}
                  onChange={(e) => {
                    setStaffData({ ...staffData, phone: e.target.value });
                    setWarn(false);
                    setErr(false);
                  }}
                  placeholder={t("lang_Write_Your_Phone")}
                />

                <InputComponent
                  id="email"
                  name="email"
                  label={t("lang_email")}
                  required={true}
                  value={staffData?.email}
                  onChange={(e) => {
                    setStaffData({ ...staffData, email: e.target.value });
                    setValidEmailErr(false);
                    setWarn(false);
                    setErr(false);
                  }}
                  onBlur={emailVerify}
                  placeholder={t("lang_Write_Your_Email")}
                  error={
                    err && !staffData?.email
                      ? t("lang_Please_Enter_Email")
                      : validEmailErr
                        ? t("lang_Email_is_invalid")
                        : warn && !staffData?.email
                          ? t("lang_Please_Enter_Email")
                          : ""
                  }
                />

                <InputComponent
                  id="pin"
                  name="pin"
                  type="number"
                  label={t("lang_PIN")}
                  required={true}
                  value={staffData?.pin}
                  onChange={(e) => {
                    if (e.target.value?.length <= 4 || e.target.value?.length <= 6) {
                      setStaffData({ ...staffData, pin: e.target.value });
                      setWarn(false);
                      setErr(false);
                    }
                  }}
                  placeholder={t("lang_Write_Your_Pin")}
                  suffix={
                    <ButtonComponent
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setStaffData({ ...staffData, pin: generatePin() });
                      }}
                    >
                      {t("lang_generate_PIN")}
                    </ButtonComponent>
                  }
                  error={
                    (err || warn) && !staffData?.pin
                      ? t("lang_Please_Enter_Your_Pin")
                      : ""
                  }
                />

                <SelectComponent
                  id="pos_location"
                  name="pos_location"
                  label={t("lang_Location")}
                  required={true}
                  multiple={true}
                  placeholder={t("lang_Select_Location")}
                  options={locationOptions}
                  value={staffData?.pos_location}
                  onChange={(e) => {
                    setStaffData({
                      ...staffData,
                      pos_location: e.target.value,
                    });
                  }}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar matching FAQ */}
      <div className="flex items-center justify-between w-full border-t border-[var(--border-color)] bg-[var(--secondary-bg-color)] p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={() => handleRequestClose()}
          className="flex items-center text-xs font-medium text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          <ButtonComponent
            variant="primary"
            size="sm"
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleSubmitBulkStaffs}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : "Save Changes"}
          </ButtonComponent>
        </div>
      </div>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onStay={handleStay}
        onLeave={handleConfirmLeave}
      />
    </div>
  );
};

export default BulkEditPosStaff;
